import { type ReactNode, useMemo } from "react";
import { Separator } from "@/v2/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/ui/tabs";
import { cn } from "@/v2/lib/utils";
import type { Entry } from "./Entries/normalize";

const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
] as const;

function fmt(h: number): string {
	return `${h.toFixed(2)} h`;
}

function pad2(n: number): string {
	return String(n).padStart(2, "0");
}

interface YearTotals {
	/** Hours per month index (0–11) for the current year. */
	monthly: number[];
	/** Year-to-date total (entries dated on or before today). */
	ytd: number;
	/** Full-year total. */
	yearly: number;
}

function computeYearTotals(
	entries: Entry[],
	year: number,
	todayStr: string,
	includeDeleted: boolean,
): YearTotals {
	const monthly = new Array<number>(12).fill(0);
	let ytd = 0;
	let yearly = 0;
	for (const e of entries) {
		if (e.status === "deleted" && !includeDeleted) continue;
		if (Number(e.date.slice(0, 4)) !== year) continue;
		const monthIdx = Number(e.date.slice(5, 7)) - 1;
		monthly[monthIdx] += e.units;
		yearly += e.units;
		if (e.date <= todayStr) ytd += e.units;
	}
	return { monthly, ytd, yearly };
}

interface Calendar2YearTimelineProps {
	/** Entries matching the current sidebar and calendar filters. */
	entries: Entry[];
	includeDeleted?: boolean;
	/** The month the grid is currently showing; highlighted in the timeline. */
	viewedMonth: Date;
	/** Re-anchors the calendar to the first of the chosen month. */
	onSelectMonth: (month: Date) => void;
	/** Optional controls rendered directly before the yearly total divider. */
	yearControls?: ReactNode;
}

/**
 * A minimal year-at-a-glance strip that replaces the month picker: one
 * clickable tab per month (with its hour total) plus a year-to-date anchor.
 * Selecting a tab re-anchors the calendar grid to that month. Scoped to the
 * current calendar year — the only year the timeline charts.
 */
export function Calendar2YearTimeline({
	entries,
	includeDeleted = false,
	viewedMonth,
	onSelectMonth,
	yearControls,
}: Calendar2YearTimelineProps) {
	const today = useMemo(() => new Date(), []);
	const todayYear = today.getFullYear();
	const year = viewedMonth.getFullYear();
	const todayStr = `${todayYear}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

	const { monthly, ytd, yearly } = useMemo(
		() => computeYearTotals(entries, year, todayStr, includeDeleted),
		[entries, year, todayStr, includeDeleted],
	);

	const viewedMonthIdx = viewedMonth.getMonth();
	const yearTotal = year === todayYear ? ytd : yearly;

	return (
		<div className="flex min-w-0 flex-1 items-center gap-3">
			{yearControls}

			<Tabs
				value={viewedMonthIdx >= 0 ? String(viewedMonthIdx) : ""}
				onValueChange={(v) => onSelectMonth(new Date(year, Number(v), 1))}
				className="min-w-0 flex-1"
			>
				<TabsList className="h-12 w-full gap-0.5 border border-input bg-background p-1">
					{MONTH_LABELS.map((label, i) => {
						const total = monthly[i];
						const isActive = i === viewedMonthIdx;

						return (
							<TabsTrigger
								key={label}
								value={String(i)}
								className={cn(
									"h-full min-w-0 flex-col gap-1 px-1.5 py-1.5 hover:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none",
									isActive && "bg-primary/8! hover:bg-primary/8!",
								)}
							>
								<span className="text-[10px] font-medium leading-none text-foreground">
									{label}
								</span>
								<span className="text-[12px] font-medium leading-none tabular-nums text-foreground">
									{total > 0 ? `${total.toFixed(1)}h` : "0h"}
								</span>
							</TabsTrigger>
						);
					})}
				</TabsList>
			</Tabs>

			<Separator orientation="vertical" className="h-9 shrink-0" />

			<div className="flex min-w-[5.25rem] shrink-0 flex-col items-end">
				<span className="text-base font-semibold leading-tight tabular-nums text-foreground">
					{fmt(yearTotal)}
				</span>
				<span className="text-xs text-muted-foreground">
					{year === todayYear ? `${year} YTD` : `${year} total`}
				</span>
			</div>
		</div>
	);
}
